import { useEffect, useState } from "react";
import { useGetResourceUrlMutation } from "../../api/vmsSlice";

// getFileUrl signs for 300s (services/S3/index.js). Re-sign a little before that so a remount never uses a dead URL.
const SIGNED_URL_TTL_MS = 4 * 60 * 1000;

// Module-level so navigating away and back does not re-sign every camera.
const signedUrlCache = new Map();

/**
 * Turns a camera's bare S3 key into a URL the board can put in an <img>.
 * The edge overwrites one fixed key per camera, so a signed URL keeps working and keeps
 * serving whatever the newest frame is — but an unchanged src is also a src the browser
 * will not refetch. Signing again when snapshotAt moves yields a new X-Amz-Date, which is
 * what actually makes the new frame appear. Nothing re-signs while a camera is idle.
 * A cache-busting query parameter would not work here: SigV4 signs the query string, so
 * any extra parameter invalidates the signature.
 */
export function useSnapshotUrl(fileKey, snapshotAt) {
	const [getResourceUrl] = useGetResourceUrlMutation();
	const [url, setUrl] = useState(null);

	useEffect(() => {
		if (!fileKey) {
			setUrl(null);
			return undefined;
		}
		// Tolerates the edge later writing a full URL instead of a key.
		if (/^https?:\/\//i.test(fileKey)) {
			setUrl(fileKey);
			return undefined;
		}

		let cancelled = false;
		const cacheKey = `${fileKey}|${snapshotAt || ""}`;
		const cached = signedUrlCache.get(cacheKey);

		if (cached && Date.now() - cached.resolvedAt < SIGNED_URL_TTL_MS) {
			setUrl(cached.url);
			return undefined;
		}

		(async () => {
			try {
				const response = await getResourceUrl({ fileKey }).unwrap();
				const signed = typeof response?.data === "string" ? response.data : null;
				if (!signed) throw new Error(response?.message || "Snapshot URL was not returned.");
				signedUrlCache.set(cacheKey, { url: signed, resolvedAt: Date.now() });
				if (!cancelled) setUrl(signed);
			} catch {
				if (!cancelled) setUrl(null);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [fileKey, snapshotAt, getResourceUrl]);

	return url;
}
